/**
 * workflow:run command - Run workflow by workflow ID
 */

import { defineCommand, validationError, handleError, type CLIInput, type PluginContextV3 , type CommandResult} from '@kb-labs/sdk';
import type { WorkflowRunRequest } from '@kb-labs/workflow-contracts';
import { WorkflowDaemonClient } from '../http-client.js';

interface WorkflowRunFlagsInput {
  json?: boolean;
  'workflow-id'?: string;
  input?: string;
  inputs?: string;
  isolation?: string;
  'trigger-type'?: string;
  'trigger-user'?: string;
  'target-environment-id'?: string;
  'target-workspace-id'?: string;
  'target-namespace'?: string;
  'target-workdir'?: string;
}

function parseJsonInput(value: string | undefined): unknown {
  if (!value) {
    return undefined;
  }
  return JSON.parse(value);
}

function parseIsolation(value: string | undefined): WorkflowRunRequest['isolation'] {
  if (!value) {
    return undefined;
  }
  if (value === 'strict' || value === 'balanced' || value === 'relaxed') {
    return value;
  }
  throw new Error(`Invalid isolation value: ${value}. Expected strict|balanced|relaxed`);
}

function parseTriggerType(value: string | undefined): WorkflowRunRequest['trigger'] {
  if (!value) {
    return undefined;
  }
  if (value === 'manual' || value === 'api' || value === 'cron') {
    return { type: value };
  }
  throw new Error(`Invalid trigger type: ${value}. Expected manual|api|cron`);
}

export default defineCommand<unknown, CLIInput<WorkflowRunFlagsInput>, unknown>({
  id: 'workflow:run',
  description: 'Run workflow by ID with optional target/isolation overrides. Pass JSON payload via --input (not --inputs).',

  handler: {
    async execute(ctx: PluginContextV3, input: CLIInput<WorkflowRunFlagsInput>): Promise<CommandResult> {
      const outputJson = input.flags.json ?? false;
      const workflowId = input.flags['workflow-id'];

      if (!workflowId) {
        validationError(ctx, 'Missing required flag: --workflow-id', 'Usage: kb workflow:run --workflow-id <id>', outputJson);
        return { ok: false, error: 'Command failed' };
      }

      try {
        const request: WorkflowRunRequest = {};

        if (input.flags.inputs && !input.flags.input) {
          ctx.ui?.warn?.('--inputs is deprecated; use --input instead');
        }
        const rawInput = input.flags.input ?? input.flags.inputs;
        const inputPayload = parseJsonInput(rawInput);
        if (inputPayload !== undefined) {
          request.input = inputPayload;
        }

        const isolation = parseIsolation(input.flags.isolation);
        if (isolation) {
          request.isolation = isolation;
        }

        const trigger = parseTriggerType(input.flags['trigger-type']);
        if (trigger) {
          request.trigger = {
            ...trigger,
            user: input.flags['trigger-user'],
          };
        } else if (input.flags['trigger-user']) {
          request.trigger = {
            type: 'manual',
            user: input.flags['trigger-user'],
          };
        }

        const target = {
          environmentId: input.flags['target-environment-id'],
          workspaceId: input.flags['target-workspace-id'],
          namespace: input.flags['target-namespace'],
          workdir: input.flags['target-workdir'],
        };
        if (target.environmentId || target.workspaceId || target.namespace || target.workdir) {
          request.target = target;
        }

        const client = new WorkflowDaemonClient();
        const result = await client.runWorkflow(workflowId, request);

        if (outputJson) {
          ctx.ui?.json?.({ ok: true, data: result });
        } else {
          ctx.ui?.success?.('Workflow run submitted', {
            title: 'Workflow Engine',
            sections: [{
              header: 'Details',
              items: [
                `Workflow ID: ${workflowId}`,
                `Run ID: ${result.runId}`,
                `Status: ${result.status}`,
                ...(request.isolation ? [`Isolation: ${request.isolation}`] : []),
                ...(request.target?.namespace ? [`Target Namespace: ${request.target.namespace}`] : []),
                ...(request.target?.environmentId ? [`Target Environment: ${request.target.environmentId}`] : []),
                ...(request.target?.workspaceId ? [`Target Workspace: ${request.target.workspaceId}`] : []),
              ],
            }],
          });
        }

        return { ok: true };
      } catch (error) {
        handleError(ctx, error, outputJson);
        return { ok: false, error: 'Command failed' };
      }
    },
  },
});
